import {
  DataTablePagination,
  DataTableSearch,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxEmpty,
  ComboboxList,
  Typography,
  DataTable,
  Combobox,
  Loading,
  Button,
} from '@ui/components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useCallback, ReactNode, useState } from 'react';
import { cn } from '@ui/utils';

import { UseAssignTable } from './use-assign-table';

export interface AssignTableProps<T> extends UseAssignTable<T> {
  onSelectedChange: (value: string[]) => void;
  selected?: string[];
  text: {
    selectedRows: (count: number) => string;
    comboboxPlaceholder: string;
    searchPlaceholder: string;
    comboboxNoResults: string;
    assignButtonText: string;
    removeButtonText: string;
    tableNoResults: string;
    title: string;
  };
  itemTemplate: (item: T) => ReactNode;
  className?: string;
  disabled?: boolean;
}

export function AssignTable<T>({
  onSelectedChange,
  selected: value,
  isLoading,
  combobox,
  table,
  text,
  disabled,
  itemTemplate,
  className,
}: AssignTableProps<T>) {
  const {
    title,
    comboboxPlaceholder,
    comboboxNoResults,
    searchPlaceholder,
    assignButtonText,
    removeButtonText,
    tableNoResults,
    selectedRows,
  } = text;
  const [selected, setSelected] = useState<string[]>(value ?? []);

  useDeepCompareEffect(() => {
    setSelected(value ?? []);
  }, [value, setSelected]);

  const selectedItems = Object.keys(table.getState().rowSelection).map((key) => key);

  const handleRemove = useCallback(() => {
    onSelectedChange?.(selected.filter((item) => !selectedItems.includes(item)));
    table.setRowSelection({});
    table.setPageIndex(0);
  }, [table, selectedItems, selected, onSelectedChange]);

  return (
    <div
      className={cn(
        'bg-table-background rounded-lg overflow-hidden border border-border-weak',
        className,
      )}
    >
      <div className="ml-auto flex items-center justify-between gap-4 p-4">
        <Typography variant="labelMediumMedium">
          {selectedItems.length > 0 ? selectedRows(selectedItems.length) : title}
        </Typography>
        <DataTablePagination
          pageIndex={table.getState().pagination.pageIndex}
          pageSize={table.getState().pagination.pageSize}
          prevPageDisabled={!table.getCanPreviousPage()}
          nextPageDisabled={!table.getCanNextPage()}
          prevPage={() => table.previousPage()}
          nextPage={() => table.nextPage()}
          totalRows={table.getRowCount()}
        />
      </div>
      <div className="px-4 pb-3">
        <DataTableSearch
          value={table.getState().globalFilter}
          onChange={table.setGlobalFilter}
          placeholder={searchPlaceholder}
          className="w-61"
        />
      </div>

      {!disabled && !!selectedItems.length && (
        <div>
          <Button onClick={handleRemove} variant="ghost" size="small">
            {removeButtonText}
          </Button>
        </div>
      )}

      <DataTable
        noResultsComponent={<div className="text-center">{tableNoResults}</div>}
        columnsLength={table.getVisibleFlatColumns().length}
        className="max-h-full overflow-auto"
        isLoading={isLoading ?? false}
        table={table}
      />

      <Combobox
        onSelectedChange={(item) => {
          if (!item) return;
          setSelected((prev) => [...prev, item]);
          onSelectedChange?.([...selected, item]);
          combobox.setPhrase('');
        }}
      >
        <ComboboxTrigger asChild>
          <Button className="rounded-none" disabled={disabled} variant="tertiary">
            {assignButtonText}
          </Button>
        </ComboboxTrigger>
        <ComboboxContent side="top">
          <Loading isLoading={combobox.isLoading}>
            <ComboboxList>
              <ComboboxEmpty>{comboboxNoResults}</ComboboxEmpty>
              {itemTemplate && combobox.options.map(itemTemplate)}
            </ComboboxList>
          </Loading>
          <ComboboxInput
            onChange={(e) => combobox.setPhrase(e.target.value)}
            placeholder={comboboxPlaceholder}
            value={combobox.phrase}
            removeIcon
          />
        </ComboboxContent>
      </Combobox>
    </div>
  );
}
